/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useCallback, useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import Colors from '@global/colors';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import { debounce } from 'lodash';

const DEFAULT_LIMIT_OPTIONS = [10, 20, 30, 40, 50];

// build a windowed list of page numbers with ellipsis placeholders
const buildPageList = (current, total) => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  let start = Math.max(2, current - 1);
  let end = Math.min(total - 1, current + 1);
  if (current <= 3) {
    start = 2;
    end = 4;
  }
  if (current >= total - 2) {
    start = total - 3;
    end = total - 1;
  }
  const pages = [1];
  if (start > 2) {
    pages.push('...');
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (end < total - 1) {
    pages.push('...');
  }
  pages.push(total);
  return pages;
};

const Pagination = (props) => {
  const testID = props.testID || 'pagination';
  const showFirstLast = props.showFirstLast || false;
  const showPageNumbers = props.showPageNumbers ?? true;
  const dropUp = props.dropUp || false;
  const limitOptions =
    props.limitOptions && props.limitOptions.length
      ? props.limitOptions
      : DEFAULT_LIMIT_OPTIONS;
  const [isDisabledLeft, setIsDisabledLeft] = useState(false);
  const [isDisabledRight, setIsDisabledRight] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [showLimitMenu, setShowLimitMenu] = useState(false);

  const totalData = Number(
    props.totalData && !isNaN(props.totalData) ? props.totalData : 0,
  ); // total data
  const skip = Number(props.skip && !isNaN(props.skip) ? props.skip : 0); // skip of pagination
  const limit = Number(props.limit && !isNaN(props.limit) ? props.limit : 15); // limit per page
  const calculatePage = Math.ceil(totalData / limit); // how many total page we have
  const pageCount = calculatePage ? calculatePage : 1; // final total page

  // check the left/right button disabled or not
  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsDisabledLeft(skip <= 0 || skip - limit < 0);
      setIsDisabledRight(skip + limit >= limit * calculatePage);

      const calculatePageNumber = skip / limit + 1; // calculate active page number
      setPageNumber(calculatePageNumber);
    }
    return () => {
      mounted = false;
    };
  }, [skip, limit, calculatePage]);

  const goToFirst = () => {
    if (!isDisabledLeft) {
      props.onSetSkip(0);
    }
  };

  const goToPrev = () => {
    if (!isDisabledLeft) {
      props.onSetSkip(skip - limit);
    }
  };

  const goToNext = () => {
    if (!isDisabledRight) {
      props.onSetSkip(skip + limit);
    }
  };

  const goToLast = () => {
    if (!isDisabledRight) {
      props.onSetSkip(limit * calculatePage - limit);
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > pageCount || page === pageNumber) {
      return;
    }
    props.onSetSkip((page - 1) * limit);
  };

  const onChangeLimit = (newLimit) => {
    setShowLimitMenu(false);
    if (newLimit === limit) {
      return;
    }
    if (props.onSetLimit) {
      props.onSetLimit(newLimit);
    }
  };

  const setPage = useCallback(
    debounce((val) => {
      let newVal = val;
      if (!newVal || newVal == 0) {
        newVal = 1;
        setPageNumber(newVal);
      }
      if (newVal) {
        props.onSetSkip((parseInt(newVal) - 1) * parseInt(limit));
      }
    }, 1000),
    [],
  );

  const activePage = Math.floor(skip / limit) + 1;
  const pageList = buildPageList(activePage, pageCount);

  return (
    <View testID={testID} style={styles.pagination}>
      {/* Rows per page selector */}
      {props.onSetLimit ? (
        <View style={styles.limitContainer}>
          <SociollaText type="medium" style={styles.paginationText}>
            Rows per page
          </SociollaText>
          <View>
            <TouchableOpacity
              testID={`${testID}-limit-btn`}
              activeOpacity={0.6}
              onPress={() => setShowLimitMenu(!showLimitMenu)}
              style={styles.limitButton}>
              <SociollaText type="medium" style={styles.limitButtonText}>
                {limit}
              </SociollaText>
              <Image
                style={{ width: 10, height: 5, marginLeft: 6 }}
                width={10}
                height={5}
                svg={true}
                source={require('@assets/img/icons/arrow-down-grey.svg')}
              />
            </TouchableOpacity>
            {showLimitMenu ? (
              <View
                style={[
                  styles.limitMenu,
                  dropUp ? styles.limitMenuUp : styles.limitMenuDown,
                ]}>
                {limitOptions.map((option) => (
                  <TouchableOpacity
                    key={`limit-option-${option}`}
                    testID={`${testID}-limit-option-${option}`}
                    activeOpacity={0.6}
                    onPress={() => onChangeLimit(option)}
                    style={[
                      styles.limitMenuItem,
                      option === limit && styles.limitMenuItemActive,
                    ]}>
                    <SociollaText
                      type="medium"
                      style={[
                        styles.limitMenuItemText,
                        option === limit && styles.limitMenuItemTextActive,
                      ]}>
                      {option}
                    </SociollaText>
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              false
            )}
          </View>
        </View>
      ) : (
        false
      )}
      <SociollaText type="medium" style={styles.paginationText}>
        {totalData} items
      </SociollaText>
      {showFirstLast && (
        <TouchableOpacity
          testID={`${testID}-first-btn`}
          onPress={() => goToFirst()}
          activeOpacity={0.6}
          style={[
            styles.paginationBtn,
            isDisabledLeft && styles.paginationBtnDisabled,
          ]}
          disabled={isDisabledLeft}>
          <Image
            style={[styles.paginationIcon, { width: 15, height: 12 }]}
            width={15}
            height={12}
            svg={true}
            source={require('@assets/img/icons/arrow-left-double-grey.svg')}
          />
        </TouchableOpacity>
      )}
      <TouchableOpacity
        testID={`${testID}-prev-btn`}
        onPress={() => goToPrev()}
        activeOpacity={0.6}
        style={[
          styles.paginationBtn,
          isDisabledLeft && styles.paginationBtnDisabled,
        ]}
        disabled={isDisabledLeft}>
        <Image
          style={[styles.paginationIcon, { width: 7, height: 12 }]}
          width={7}
          height={12}
          svg={true}
          source={require('@assets/img/icons/arrow-left-grey.svg')}
        />
      </TouchableOpacity>
      {showPageNumbers ? (
        <View style={styles.pageNumberContainer}>
          {pageList.map((page, idx) =>
            page === '...' ? (
              <View key={`page-ellipsis-${idx}`} style={styles.pageEllipsis}>
                <SociollaText type="medium" style={styles.paginationText}>
                  ...
                </SociollaText>
              </View>
            ) : (
              <TouchableOpacity
                key={`page-${page}`}
                testID={`${testID}-page-${page}`}
                activeOpacity={0.6}
                onPress={() => goToPage(page)}
                style={[
                  styles.pageNumberBtn,
                  page === activePage && styles.pageNumberBtnActive,
                ]}>
                <SociollaText
                  type="medium"
                  style={[
                    styles.pageNumberText,
                    page === activePage && styles.pageNumberTextActive,
                  ]}>
                  {page}
                </SociollaText>
              </TouchableOpacity>
            ),
          )}
        </View>
      ) : (
        <>
          <View style={styles.paginationInputContainer}>
            <TextInput
              testID={`${testID}-input`}
              label=""
              style={styles.paginationInput}
              value={String(pageNumber)}
              onChangeText={(val) => {
                setPageNumber(val);
                setPage(val);
              }}
            />
          </View>
          <SociollaText type="medium" style={styles.paginationText}>
            of {pageCount}
          </SociollaText>
        </>
      )}
      <TouchableOpacity
        testID={`${testID}-next-btn`}
        onPress={() => goToNext()}
        activeOpacity={0.6}
        style={[
          styles.paginationBtn,
          isDisabledRight && styles.paginationBtnDisabled,
        ]}
        disabled={isDisabledRight}>
        <Image
          style={[styles.paginationIcon, { width: 7, height: 12 }]}
          width={7}
          height={12}
          svg={true}
          source={require('@assets/img/icons/arrow-right-grey.svg')}
        />
      </TouchableOpacity>
      {showFirstLast && (
        <TouchableOpacity
          testID={`${testID}-last-btn`}
          onPress={() => goToLast()}
          activeOpacity={0.6}
          style={[
            styles.paginationBtn,
            isDisabledRight && styles.paginationBtnDisabled,
          ]}
          disabled={isDisabledRight}>
          <Image
            style={[styles.paginationIcon, { width: 15, height: 12 }]}
            width={15}
            height={12}
            svg={true}
            source={require('@assets/img/icons/arrow-right-double-grey.svg')}
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  paginationText: {
    fontSize: 13,
    lineHeight: 15,
    marginRight: 5,
    color: Colors.grey1,
  },
  limitContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
    zIndex: 2,
    elevation: 2,
  },
  limitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 30,
    paddingHorizontal: 10,
    borderRadius: 4,
    borderColor: Colors.grey0,
    borderWidth: 1,
    backgroundColor: Colors.white,
  },
  limitButtonText: {
    fontSize: 13,
    color: Colors.grey1,
  },
  limitMenu: {
    position: 'absolute',
    left: 0,
    minWidth: 60,
    backgroundColor: Colors.white,
    borderRadius: 4,
    borderColor: Colors.grey0,
    borderWidth: 1,
    shadowColor: Colors.black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 7,
    elevation: 3,
    zIndex: 3,
  },
  limitMenuDown: {
    top: 36,
  },
  limitMenuUp: {
    bottom: 36,
  },
  limitMenuItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  limitMenuItemActive: {
    backgroundColor: Colors.grey4,
  },
  limitMenuItemText: {
    fontSize: 13,
    color: Colors.grey1,
  },
  limitMenuItemTextActive: {
    color: Colors.blue0,
  },
  paginationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 35,
    height: 30,
    borderRadius: 4,
    borderColor: Colors.grey1,
    backgroundColor: Colors.grey0,
    marginHorizontal: 3,
  },
  paginationBtnDisabled: {
    backgroundColor: Colors.grey7,
    opacity: 0.7,
  },
  paginationIcon: {
    alignSelf: 'center',
  },
  pageNumberContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pageNumberBtn: {
    minWidth: 30,
    height: 30,
    paddingHorizontal: 6,
    borderRadius: 4,
    borderColor: Colors.grey0,
    borderWidth: 1,
    backgroundColor: Colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 3,
  },
  pageNumberBtnActive: {
    backgroundColor: Colors.blue3,
    borderColor: Colors.blue3,
  },
  pageNumberText: {
    fontSize: 13,
    color: Colors.grey1,
  },
  pageNumberTextActive: {
    color: Colors.white,
  },
  pageEllipsis: {
    minWidth: 20,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 1,
  },
  paginationInputContainer: {
    width: 40,
    height: 30,
    marginHorizontal: 5,
    backgroundColor: Colors.white,
  },
  paginationInput: {
    width: 40,
    height: 30,
    borderColor: Colors.grey0,
    borderWidth: 1,
    borderRadius: 4,
    textAlign: 'center',
    fontFamily: 'figtree-medium',
    paddingVertical: 0,
    textAlignVertical: 'center',
  },
});

export default React.memo(Pagination, (prev, next) => prev == next);
